import { createEffect } from 'effector'
import { stateful } from '../../lib/stateful'
import type { ContentList } from '../content'
import type { TvShowList } from '../tvshows'
import type {
  Creator,
  CreatorAwards,
  CreatorSocialMediaLink,
  CreatorTag,
} from './index.h'

export type { Creator, CreatorAwards, CreatorSocialMediaLink, CreatorTag }

type PageParams = { limit?: number; offset?: number }
type SortParams = { by?: string; order?: string }
type FilterParams = { q?: string }
type GetManyParams = PageParams & SortParams & FilterParams
type GetOneParams = { id: number }
type CreatorParams = { id: number }
type CreatorTvShowsByTagParams = CreatorParams & {
  genreId: number
} & PageParams &
  SortParams
type ContentParams = { withContent?: boolean; contentCount?: number }
type CreatorContentParams = CreatorParams &
  ContentParams &
  PageParams &
  SortParams

// *
// * base API effects
// *

export const base = {
  getManyFx: createEffect<GetManyParams, Creator[]>(),
  getOneFx: createEffect<GetOneParams, Creator>(),
  getContentFx: createEffect<
    CreatorParams & PageParams & SortParams,
    ContentList
  >(),
  getTvShowsFx: createEffect<
    CreatorParams & PageParams & SortParams,
    TvShowList
  >(),
  getContentTagsFx: createEffect<
    CreatorParams & ContentParams & PageParams & SortParams,
    CreatorTag[]
  >(),
  getTvShowTagsFx: createEffect<
    CreatorParams & PageParams & SortParams,
    CreatorTag[]
  >(),
  getTvShowsByTagFx: createEffect<CreatorTvShowsByTagParams, TvShowList>(),
}

// *
// *
// *

export const pageable = (initial?: GetManyParams) =>
  stateful({
    effect: base.getManyFx,
    initial,
    methods: {
      limit: (n: number) => ({ limit: n }),
      offset: (n: number) => ({ offset: n }),
      next: (_: void, { offset = 0, limit = 36 }) => ({
        offset: offset + limit,
      }),
      prev: (_: void, { offset = 0, limit = 36 }) => ({
        offset: Math.max(offset - limit, 0),
      }),
      sort: ({ by, order }: SortParams) => ({ by, order }),
      by: (by: string | undefined) => ({ by }),
      order: (order: string | undefined) => ({ order }),
      filter: (q: string | undefined) => ({ q }),
    },
  })

export const pageableContent = (initial?: CreatorContentParams) =>
  stateful({
    effect: base.getContentTagsFx,
    initial,
    methods: {
      limit: ({ id, n }) => ({ id, limit: n }),
      offset: ({ id, n }) => ({ id, offset: n }),
      next: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: offset + limit,
      }),
      prev: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: Math.max(offset - limit, 0),
      }),
      sort: ({ id, by, order }) => ({ id, by, order }),
      by: ({ id, by }) => ({ id, by }),
      order: ({ id, order }) => ({ id, order }),
    },
  })

export const pageableCreatorContent = (initial?: CreatorContentParams) =>
  stateful({
    effect: base.getContentFx,
    initial,
    methods: {
      limit: ({ id, n }) => ({ id, limit: n }),
      offset: ({ id, n }) => ({ id, offset: n }),
      next: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: offset + limit,
      }),
      prev: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: Math.max(offset - limit, 0),
      }),
      sort: ({ id, by, order }) => ({ id, by, order }),
      by: ({ id, by }) => ({ id, by }),
      order: ({ id, order }) => ({ id, order }),
    },
  })

export const pageableCreatorTvShows = (initial?: CreatorContentParams) =>
  stateful({
    effect: base.getTvShowsFx,
    initial,
    methods: {
      limit: ({ id, n }) => ({ id, limit: n }),
      offset: ({ id, n }) => ({ id, offset: n }),
      next: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: offset + limit,
      }),
      prev: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: Math.max(offset - limit, 0),
      }),
      sort: ({ id, by, order }) => ({ id, by, order }),
      by: ({ id, by }) => ({ id, by }),
      order: ({ id, order }) => ({ id, order }),
    },
  })

export const pageableTvShowTags = (initial?: CreatorContentParams) =>
  stateful({
    effect: base.getTvShowTagsFx,
    initial,
    methods: {
      limit: ({ id, n }) => ({ id, limit: n }),
      offset: ({ id, n }) => ({ id, offset: n }),
      next: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: offset + limit,
      }),
      prev: (_: void, { id, offset = 0, limit = 36 }) => ({
        id,
        offset: Math.max(offset - limit, 0),
      }),
      sort: ({ id, by, order }) => ({ id, by, order }),
      by: ({ id, by }) => ({ id, by }),
      order: ({ id, order }) => ({ id, order }),
    },
  })

export const pageableTvShowByTag = (initial?: CreatorTvShowsByTagParams) =>
  stateful({
    effect: base.getTvShowsByTagFx,
    initial,
    methods: {
      limit: ({ id, genreId, n }) => ({ id, genreId, limit: n }),
      offset: ({ id, genreId, n }) => ({ id, genreId, offset: n }),
      next: (_: void, { id, genreId, offset = 0, limit = 36 }) => ({
        id,
        genreId,
        offset: offset + limit,
      }),
      prev: (_: void, { id, genreId, offset = 0, limit = 36 }) => ({
        id,
        genreId,
        offset: Math.max(offset - limit, 0),
      }),
      sort: ({ id, genreId, by, order }) => ({ id, genreId, by, order }),
      by: ({ id, genreId, by }) => ({ id, genreId, by }),
      order: ({ id, genreId, order }) => ({ id, genreId, order }),
    },
  })
